'use client'

import { Button } from '@heroui/button'
import { Modal, ModalBody, ModalContent, ModalHeader } from '@heroui/modal'
import { Spinner } from '@heroui/spinner'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { format, isFuture } from 'date-fns'
import { useCallback, useMemo } from 'react'

import { StarFilledIcon } from '@/components/shared'
import {
  ArrowLeftIcon,
  CheckIcon,
  EmotionSadIcon,
  StarIcon,
} from '@/components/shared/icons'
import { Typography } from '@/components/ui/typography'
import { useApi } from '@/trpc/react'
import { cn } from '@/utils'


interface StatisticElementProps {
  label: string
  value: string
  icon: React.ReactNode
}

interface CreateMomentStatusModalProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  status: 'pending' | 'success' | 'error' | 'idle'
  momentId?: string
  onSuccess: () => void
  date: Date
  statistics: StatisticElementProps[]
}

const StatisticElement = ({ label, value, icon }: StatisticElementProps) => {
  return (
    <div className="flex flex-col items-center justify-center gap-1">
      <div className="flex min-w-28 items-center justify-center gap-[10px] rounded-full bg-primary-100 px-4 py-2">
        {icon}
        <Typography size="base" weight="medium" className="leading-6">
          {value}
        </Typography>
      </div>
      <Typography
        size="xsmall"
        className="text-center text-content4-foreground"
        weight="medium"
      >
        {label}
      </Typography>
    </div>
  )
}

const CreateMomentStatusModal = ({
  date,
  isOpen,
  status,
  momentId,
  statistics,
  onOpenChange,
  onSuccess,
}: CreateMomentStatusModalProps) => {
  const api = useApi()
  const queryClient = useQueryClient()

  const { data: moment, isLoading: isLoadingMoment } = useQuery(
    api.moments.getById.queryOptions(
      { id: momentId || '' },
      { enabled: !!momentId },
    ),
  )

  const {
    mutate: updateFavoriteMoment,
    variables,
    isPending,
  } = useMutation(
    api.moments.updateFavorite.mutationOptions({
      onSettled: (_data, _error, variables) => {
        queryClient.invalidateQueries({
          queryKey: api.moments.getById.queryKey({ id: variables.id }),
        })
        queryClient.invalidateQueries({
          queryKey: api.moments.getFavorites.queryKey(),
        })
      },
    }),
  )

  const isFutureLetter = isFuture(date)

  const title = useMemo(() => {
    if (status === 'pending') {
      if (isFutureLetter) return 'Sending moment'
      return 'Creating moment...'
    }
    if (status === 'success') return 'Great!'
    return 'Sorry, there was an error'
  }, [isFutureLetter, status])

  const description = useMemo(() => {
    if (isFutureLetter && status !== 'error') {
      const formattedDate = format(date, 'do MMM, yyyy')
      return `You'll receive this letter by ${formattedDate}`
    }
    if (status === 'pending') {
      return "You're saving your journal"
    }
    if (status === 'success') return "You've completed your journal"
    return 'Make sure you have connection and try again'
  }, [isFutureLetter, status, date])

  const icon =
    status === 'pending' ? (
      <Spinner />
    ) : status === 'success' ? (
      <CheckIcon />
    ) : (
      <EmotionSadIcon />
    )

  const buttonLabel = status === 'error' ? 'Try again' : 'Continue'

  const handleClose = useCallback(() => {
    if (status !== 'error') {
      return onSuccess?.()
    }
    onOpenChange(false)
  }, [onOpenChange, status, onSuccess])

  const handleUpdateFavorite = useCallback(() => {
    updateFavoriteMoment({
      id: moment?.id || '',
      isFavorite: !moment?.isFavorite,
    })
  }, [moment?.id, moment?.isFavorite, updateFavoriteMoment])

  const isFavorite = isPending ? variables.isFavorite : moment?.isFavorite

  return (
    <Modal
      size="full"
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      hideCloseButton
      isDismissable={false}
      isKeyboardDismissDisabled
      scrollBehavior="normal"
      classNames={{
        header: 'flex justify-between items-center',
      }}
    >
      <ModalContent>
        {status !== 'pending' && !isLoadingMoment && (
          <ModalHeader className="px-4">
            <Button
              isIconOnly
              size="md"
              variant="flat"
              radius="full"
              className="text-2xl"
              onPress={handleClose}
            >
              <ArrowLeftIcon />
            </Button>
            <Button
              isIconOnly
              size="md"
              variant="flat"
              className="text-2xl text-default-foreground"
              radius="full"
              disabled={isPending}
              onPress={handleUpdateFavorite}
            >
              {isFavorite ? <StarFilledIcon /> : <StarIcon />}
            </Button>
          </ModalHeader>
        )}
        <ModalBody className="px-4">
          <div className="flex h-full flex-col items-center justify-center">
            <div
              className={cn(
                'mb-[28px] flex h-20 w-20 items-center justify-center rounded-full text-5xl',
                status === 'pending' && 'bg-primary-100 text-default-400',
                status === 'success' && 'bg-primary-100 text-default-400',
                status === 'error' && 'bg-danger-50 text-danger',
              )}
            >
              {icon}
            </div>
            <Typography size="xlarge" weight="medium" className="text- mb-1">
              {title}
            </Typography>
            <Typography
              size="small"
              weight="regular"
              className="mb-8 text-default-500"
            >
              {description}
            </Typography>
            <div className="flex gap-2">
              {statistics.map((statistic) => (
                <StatisticElement key={statistic.label} {...statistic} />
              ))}
            </div>
            {status !== 'pending' && (
              <div className="mt-16 flex gap-2">
                <Button
                  size="lg"
                  variant="solid"
                  color="primary"
                  className="rounded-[80px]"
                  onPress={handleClose}
                >
                  {buttonLabel}
                </Button>
              </div>
            )}
          </div>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

export default CreateMomentStatusModal
